import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { ComponentDataTable, DefaultModal } from '../../../components';
import {
  debounceIndexTechnicalIncidentsRequest,
  exportTechnicalIncidentsRequest
} from '../../../requests/technicalIncidents';
import AttendancesControlClockFilter from '../AttendancesControlClock/AttendancesControlClockFilter';
import { downloadFile } from '../../../services/utils';
import './styles.scss';
import TechnicalIncidentsPrint from './TechnicalIncidentsPrint';
import Columns from './Columns';

const customStyles = {
  rows: {
    style: {
      minHeight: '45px'
    }
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      borderLeft: '1px solid rgb(212, 212, 212)',
      borderRight: '1px solid rgb(212, 212, 212)',
      borderTop: '1px solid rgb(212, 212, 212)',
      borderBottom: '1px solid rgb(212, 212, 212)'
    }
  }
};

const TechnicalIncidentsDataTable = () => {
  const [onRequest, setOnRequest] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [amount, setAmount] = useState(0);
  const [moreData, setMoreData] = useState(false);
  const [query, setQuery] = useState();
  const [rangeDateText, setRangeDateText] = useState('');
  const [rowsDataPrint, setRowsDataPrint] = useState([]);
  const [searchFirstStart, setSearchFirstStart] = useState(false);
  const componentRef = useRef();

  const [modalState, setModalState] = useState({
    show: false,
    type: '',
    text: '',
    actionType: ''
  });

  const dispatch = useDispatch();

  const handleSuccessIndex = response => {
    const { data, metadata } = response.data;
    setEmployees(data);
    setAmount(metadata.amount);
  };

  const handleRequest = async params => {
    const queryParams = {
      ...params,
      ...query,
      paginate: true
    };

    setOnRequest(true);
    debounceIndexTechnicalIncidentsRequest({
      dispatch,
      params: queryParams,
      successCallback: handleSuccessIndex,
      callback: () => setOnRequest(false)
    });
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'company':
        return { sort_company: name };
      case 'branch_office':
        return { sort_branch_office: name };
      default:
        return { sort_column: name };
    }
  };

  const printTypeText = {
    print: '¿Estás seguro que quieres imprimir el reporte de Incidentes Técnicos?',
    all: '¿Estás seguro que quieres exportar el reporte de Incidentes Técnicos en formato '
  };

  const handleOptionSelection = (action, actionType) => {
    switch (true) {
      case ['download_excel', '.xlsx'].includes(action):
        setModalState({ text: `${printTypeText.all} Excel?`, type: '.xlsx', show: true, actionType });
        break;
      case ['download_docx', '.docx'].includes(action):
        setModalState({ text: `${printTypeText.all} Word?`, type: '.docx', show: true, actionType });
        break;
      case ['download_pdf', '.pdf'].includes(action):
        setModalState({ text: `${printTypeText.all} Pdf?`, type: '.pdf', show: true, actionType });
        break;
      case ['print', '.print'].includes(action):
        setModalState({ text: printTypeText.print, type: '.print', show: true, actionType });
        break;
      default:
    }
  };

  const formRequest = values => {
    setQuery({ range_filter: values.rangeDate });
    setRangeDateText(`Rango de fechas: Desde ${  values.rangeDate[0]  } hasta ${  values.rangeDate[1]}`);
    if (['.docx', '.xlsx', '.pdf', '.print'].includes(values.type)) {
      handleOptionSelection(values.type, 'download');
      return;
    }
    setSearchFirstStart(true);
    setMoreData(!moreData);
  };

  const isDownloableFile = format => {
    return ['.xlsx', '.docx', '.pdf'].includes(format);
  };

  const handleBeforePrint = () => {
    setOnRequest(false);
  };

  const printInfo = useReactToPrint({
    content: () => componentRef.current,
    onBeforePrint: handleBeforePrint,
    pageStyle: `
    @media print {
      @page {
        size: A4 landscape !important;
        margin: 10mm !important;
      }

      body {
        margin: 0 !important;
        padding: 10mm !important;
      }
    }
  `
  });

  const handleAttendanceReportRequest = async (format, requestType = 'json') => {
    setOnRequest(true);
    const responseType = format === '.docx' ? 'blob' : requestType;
    const newFormat = format === '.print' ? '' : format;
    exportTechnicalIncidentsRequest({
      dispatch,
      params: { ...query, paginate: format === '.json' },
      format: newFormat,
      responseType,
      successCallback: response => {
        if (isDownloableFile(format)) {
          downloadFile(response);
        }
        if (format === '.print') {
          setRowsDataPrint(response.data);
          printInfo();
        }
      },
      callback: () => setOnRequest(false)
    });
  };

  return (
    <>
      <div className="technical_incidents">
        <AttendancesControlClockFilter formRequest={formRequest} />
        <ComponentDataTable
          onRequest={onRequest}
          columns={Columns()}
          data={employees}
          moreData={moreData}
          searchFirstStart={searchFirstStart}
          totalRows={amount}
          handleSortCase={sortColumnCase}
          resourceRequest={handleRequest}
          pointerOnHover
          withMassActions
        />
        <DefaultModal
          title={
            modalState.type === '.print'
              ? 'Imprimir Reporte de Incidentes Técnicos'
              : 'Exportar Reporte de Incidentes Técnicos'
          }
          body={modalState.text}
          show={modalState.show}
          handleClose={() => setModalState(oldState => ({ ...oldState, show: false }))}
          handleConfirm={() => {
            handleAttendanceReportRequest(modalState.type, 'text');
            setModalState(oldState => ({ ...oldState, show: false }));
          }}
          titleBtnClose="Cancelar"
          titleBtnSave="Confirmar"
        />

        <div style={{ display: 'none' }}>
          <TechnicalIncidentsPrint
            key="technical-incidents-print"
            ref={componentRef}
            headers={Columns()}
            rows={rowsDataPrint}
            customStyles={customStyles}
            rangeDateText={rangeDateText}
          />
        </div>
      </div>
    </>
  );
};

export default TechnicalIncidentsDataTable;
